import CountryDropdown from "@/components/signup/CountryDropdown";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Button from "@/components/ui/button";
import { checkPasswordStrength, isValidEmail } from "@/utils/inputChecker";
import { SendSignUpEmailVerification, CheckEmailVerificationStatus } from "@/api/adcelerateSignup";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useState, useEffect, useRef } from "react";

export default function PersonalInformation({ userData, handleUserDataInput, hideEmailVerification = false }) {
  const landingPage = import.meta.env.VITE_LANDING_PAGE;

  const [passwordStrength, setPasswordStrength] = useState("");
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [verificationError, setVerificationError] = useState("");
  const [verificationSuccess, setVerificationSuccess] = useState("");
  const pollingIntervalRef = useRef(null);

  const handlePasswordChange = (e) => {
    handleUserDataInput(e);
    setPasswordStrength(checkPasswordStrength(e.target.value));
  };

  const checkVerificationStatus = async () => {
    if (!userData.email || !isValidEmail(userData.email) || isEmailVerified) return;

    setIsChecking(true);
    try {
      const result = await CheckEmailVerificationStatus(userData.email);
      if (result.success && result.isVerified) {
        setIsEmailVerified(true);
        setVerificationSuccess("Email verified successfully!");
        setVerificationError("");
        // Stop polling once verified
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      }
    } catch (error) {
      console.error("Error checking verification status:", error);
    } finally {
      setIsChecking(false);
    }
  };

  const handleSendVerification = async () => {
    if (!userData.email) {
      setVerificationError("Please enter your email address.");
      return;
    }

    if (!isValidEmail(userData.email)) {
      setVerificationError("Please enter a valid email address.");
      return;
    }

    setIsSending(true);
    setVerificationError("");
    setVerificationSuccess("");

    try {
      const result = await SendSignUpEmailVerification(userData.email);
      if (result.success) {
        setIsEmailSent(true);
        setVerificationSuccess("Email verification sent! Please check your inbox and click the verification link.");
        setVerificationError("");
        
        // Start polling for verification status
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
        pollingIntervalRef.current = setInterval(() => {
          checkVerificationStatus();
        }, 3000); // Poll every 3 seconds
      } else {
        setVerificationError(result.message || "Failed to send verification email.");
        setVerificationSuccess("");
      }
    } catch (error) {
      console.error("Error sending verification email:", error);
      setVerificationError("An unexpected error occurred. Please try again.");
      setVerificationSuccess("");
    } finally {
      setIsSending(false);
    }
  };

  // Check verification status on initial load if email exists
  useEffect(() => {
    if (landingPage === "adcelerate" && userData.email && isValidEmail(userData.email) && !isEmailVerified) {
      checkVerificationStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Reset verification state when email changes
  useEffect(() => {
    if (landingPage === "adcelerate") {
      setIsEmailVerified(false);
      setIsEmailSent(false);
      setVerificationError("");
      setVerificationSuccess("");
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    }
  }, [userData.email, landingPage]);

  return (
    <>
      {landingPage !== "vvv" && (
        <p
          className={`text-lg text-left xxs:pt-16 xs:pt-16 sm:pt-16 md:pt-16 lg:pt-20 4xl:pt-20 ${
            landingPage === "adcelerate" ? "text-primary" : ""
          }`}
        >
          If you are under 18, your parent/guardian must fill in this data
        </p>
      )}
      <div
        className={`flex flex-col  w-full ${
          landingPage !== "vvv" ? "my-10 gap-6" : "gap-8"
        }`}
      >
        <div className="flex flex-col md:flex-row gap-4">
          <div className="space-y-2 w-full">
            <Label
              className={`text-base ${
                landingPage === "adcelerate" ? "text-[#757575]" : ""
              }`}
              htmlFor="firstName"
            >
              First Name
            </Label>
            <Input
              value={userData.firstName}
              onChange={handleUserDataInput}
              className="p-6 rounded-md focus-visible:ring-1 focus-visible:ring-[var(--primary)] bg-white text-black border border-gray-200"
              id="firstName"
              type="text"
              placeholder="Enter first name"
            />
          </div>
          <div className="space-y-2 w-full">
            <Label
              className={`text-base ${
                landingPage === "adcelerate" ? "text-[#757575]" : ""
              }`}
              htmlFor="lastName"
            >
              Last Name
            </Label>
            <Input
              value={userData.lastName}
              onChange={handleUserDataInput}
              className="p-6 rounded-md focus-visible:ring-1 focus-visible:ring-[var(--primary)] bg-white text-black border border-gray-200"
              id="lastName"
              type="text"
              placeholder="Enter last name"
            />
          </div>
        </div>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="space-y-2 w-full">
            <Label
              className={`text-base ${
                landingPage === "adcelerate" ? "text-[#757575]" : ""
              }`}
              htmlFor="email"
            >
              Email Address
            </Label>
            <Input
              value={userData.email}
              onChange={(e) => {
                const previousEmail = userData.email;
                handleUserDataInput(e);
                // If email changed, reset all verification state
                if (landingPage === "adcelerate" && previousEmail !== e.target.value) {
                  setIsEmailVerified(false);
                  setIsEmailSent(false);
                  setVerificationError("");
                  setVerificationSuccess("");
                  if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                  }
                }
              }}
              className="p-6 rounded-md focus-visible:ring-1 focus-visible:ring-[var(--primary)] bg-white text-black border border-gray-200"
              id="email"
              type="email"
              placeholder="example@gmail.com"
            />
            {landingPage === "adcelerate" && !hideEmailVerification && (
              <div className="space-y-2">
                <Button
                  onClick={handleSendVerification}
                  disabled={!userData.email || !isValidEmail(userData.email) || isSending}
                  className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-sm w-full"
                >
                  {isSending ? (
                    <>
                      <Loader2 className="animate-spin mr-2 inline" size={14} />
                      Sending...
                    </>
                  ) : isEmailSent && !isEmailVerified ? (
                    "Resend Verification Email"
                  ) : isEmailVerified ? (
                    "Verify Email Address Again"
                  ) : (
                    "Verify Email Address"
                  )}
                </Button>
                {isEmailVerified && (
                  <div className="flex items-center gap-2 p-2 bg-green-50 border border-green-200 rounded-md">
                    <CheckCircle2 className="text-green-500" size={16} />
                    <p className="text-green-700 text-xs font-medium">
                      Email verified successfully
                    </p>
                  </div>
                )}
                {verificationError && (
                  <div className="p-2 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-700 text-xs">{verificationError}</p>
                  </div>
                )}
                {verificationSuccess && !isEmailVerified && (
                  <div className="p-2 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="flex items-center gap-2">
                      <p className="text-blue-700 text-xs">{verificationSuccess}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="space-y-2 w-full">
            <Label
              className={`text-base ${
                landingPage === "adcelerate" ? "text-[#757575]" : ""
              }`}
              htmlFor="country"
            >
              Country
            </Label>
            <CountryDropdown
              value={userData.country}
              onChange={handleUserDataInput}
              className="py-[13.8px] rounded-md border border-gray-200"
              if="country"
            />
          </div>
        </div>

        {landingPage === "vvv" && (
          <div className="space-y-4 w-full">
            <Label className="text-base" htmlFor="contact">
              Contact Information
            </Label>
            <Input
              alue={userData.contact}
              onChange={handleUserDataInput}
              className="p-6 rounded-md focus-visible:ring-1 focus-visible:ring-[var(--primary)] bg-white text-black border border-gray-200"
              id="contact"
              type="text"
              placeholder="WhatsApp, Telegram, etc."
            />
          </div>
        )}

        {landingPage === "adcelerate" && (
          <div className="flex flex-col md:flex-row gap-4">
            <div className="space-y-2 w-full">
              <Label
                className={`text-base ${
                  landingPage === "adcelerate" ? "text-[#757575]" : ""
                }`}
                htmlFor="password"
              >
                Set Password
              </Label>
              <Input
                alue={userData.password}
                onChange={handlePasswordChange}
                className="p-6 rounded-md focus-visible:ring-1 focus-visible:ring-[var(--primary)] bg-white text-black border border-gray-200"
                id="password"
                type="password"
                placeholder="Enter password"
              />
              {passwordStrength && (
                <p
                  className={`text-xs font-medium pt-1 ${
                    passwordStrength === "Weak"
                      ? "text-red-500"
                      : passwordStrength === "Medium"
                        ? "text-yellow-500"
                        : "text-green-500"
                  }`}
                >
                  {passwordStrength} Password
                </p>
              )}
            </div>
            <div className="space-y-2 w-full">
              <Label
                className={`text-base ${
                  landingPage === "adcelerate" ? "text-[#757575]" : ""
                }`}
                htmlFor="confirmPassword"
              >
                Confirm Password
              </Label>
              <Input
                alue={userData.confirmPassword}
                onChange={handleUserDataInput}
                className="p-6 rounded-md focus-visible:ring-1 focus-visible:ring-[var(--primary)] bg-white text-black border border-gray-200"
                id="confirmPassword"
                type="password"
                placeholder="Confirm password"
              />
              {userData.password && userData.confirmPassword && (
                <p
                  className={`text-xs font-medium pt-1 ${
                    userData.password === userData.confirmPassword
                      ? "text-green-500"
                      : "text-red-500"
                  }`}
                >
                  {userData.password == userData.confirmPassword
                    ? "Password Match"
                    : "Password does not match."}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
